export const supportedLocales = ["en", "ru", "zh-CN"] as const;
export const supportedUiLocales = ["en", "ru"] as const;
export type SupportedLocale = (typeof supportedLocales)[number];

export const defaultLocale: SupportedLocale = "ru";
export const localeCookieName = "leadstack_locale";
export const profileLocaleStorageKey = "leadstack_profile_locale";

const supportedLocaleSet = new Set<string>(supportedLocales);
const rtlLanguagePrefixes = new Set(["ar", "fa", "he", "ur"]);

export type InitialLocaleInput = {
  cookieHeader?: string;
  profileLocale?: string | null;
  navigatorLanguages?: readonly string[];
};

export function normalizeLocale(value: string | null | undefined): SupportedLocale | undefined {
  if (!value) {
    return undefined;
  }

  const raw = value.trim();
  if (!raw) {
    return undefined;
  }

  if (supportedLocaleSet.has(raw)) {
    return raw as SupportedLocale;
  }

  const lower = raw.toLowerCase();
  if (lower === "zh" || lower.startsWith("zh-")) {
    return "zh-CN";
  }
  if (lower === "ru" || lower.startsWith("ru-")) {
    return "ru";
  }
  if (lower === "en" || lower.startsWith("en-")) {
    return "en";
  }

  return undefined;
}

export function isRtlLocale(locale: string): boolean {
  const language = locale.toLowerCase().split("-")[0];
  return rtlLanguagePrefixes.has(language);
}

export function parseLocaleCookie(cookieHeader: string | undefined): SupportedLocale | undefined {
  if (!cookieHeader) {
    return undefined;
  }

  const cookies = cookieHeader.split(";");
  for (const cookie of cookies) {
    const [name, ...valueParts] = cookie.trim().split("=");
    if (name === localeCookieName) {
      return normalizeLocale(decodeURIComponent(valueParts.join("=")));
    }
  }

  return undefined;
}

export function resolveInitialLocale(input: InitialLocaleInput): SupportedLocale {
  const explicitCookie = parseLocaleCookie(input.cookieHeader);
  if (explicitCookie) {
    return explicitCookie;
  }

  const profileLocale = normalizeLocale(input.profileLocale);
  if (profileLocale) {
    return profileLocale;
  }

  for (const language of input.navigatorLanguages ?? []) {
    const locale = normalizeLocale(language);
    if (locale) {
      return locale;
    }
  }

  return defaultLocale;
}

export function readBrowserProfileLocale(storage: Storage | undefined): SupportedLocale | undefined {
  try {
    return normalizeLocale(storage?.getItem(profileLocaleStorageKey));
  } catch {
    return undefined;
  }
}

export function persistBrowserLocale(locale: SupportedLocale, root: Document = document): void {
  root.cookie = `${localeCookieName}=${encodeURIComponent(locale)}; Path=/; Max-Age=31536000; SameSite=Lax`;
  try {
    root.defaultView?.localStorage.setItem(profileLocaleStorageKey, locale);
  } catch {
    // User can disable storage; the cookie still keeps the explicit preference.
  }
}

export function currentDocumentLocale(): SupportedLocale {
  return resolveInitialLocale({
    cookieHeader: document.cookie,
    profileLocale: readBrowserProfileLocale(window.localStorage),
    navigatorLanguages: navigator.languages
  });
}
